"use client";

import Link from "next/link";
import Icon from "@/components/Icon";
import Text from "@/components/Text";
import classNames from "@/helpers/classNames";
import styles from "./styles.module.scss";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  /** Click handler for in-place page changes (required when `getPageHref` isn't set). */
  onPageChange?: (page: number) => void;
  /**
   * When provided, pagination renders as crawlable `<Link>` elements instead
   * of buttons. Return the path for each page number (e.g.
   * `(n) => n === 1 ? "/cards/" : \`/cards/page/${n}/\``).
   */
  getPageHref?: (page: number) => string;
  className?: string;
}

const getPageNumbers = (current: number, total: number): (number | "ellipsis")[] => {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | "ellipsis")[] = [1];

  if (current > 3) {
    pages.push("ellipsis");
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push("ellipsis");
  }

  pages.push(total);
  return pages;
};

const Pagination = (props: PaginationProps) => {
  const { currentPage, totalPages, onPageChange, getPageHref, className } = props;

  if (totalPages <= 1) {
    return null;
  }

  const pages = getPageNumbers(currentPage, totalPages);

  const renderArrow = (targetPage: number, direction: "prev" | "next", disabled: boolean) => {
    const icon = direction === "prev" ? "triangleLeft" : "triangleRight";
    const ariaLabel = direction === "prev" ? "Previous page" : "Next page";
    const classes = classNames(styles.pageButton, styles.arrow);

    if (!disabled && getPageHref) {
      return (
        <Link href={getPageHref(targetPage)} className={classes} aria-label={ariaLabel}>
          <Icon title={icon} size="sm" />
        </Link>
      );
    }

    return (
      <button
        type="button"
        className={classes}
        onClick={() => onPageChange?.(targetPage)}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        <Icon title={icon} size="sm" />
      </button>
    );
  };

  const renderPage = (page: number) => {
    const isActive = page === currentPage;
    const classes = classNames(styles.pageButton, { [styles.active]: isActive });

    if (getPageHref) {
      return (
        <Link
          key={page}
          href={getPageHref(page)}
          className={classes}
          aria-current={isActive ? "page" : undefined}
          aria-label={`Page ${page}`}
        >
          <Text text={page} size="md" weight="medium" />
        </Link>
      );
    }

    return (
      <button
        key={page}
        type="button"
        className={classes}
        onClick={() => onPageChange?.(page)}
        aria-current={isActive ? "page" : undefined}
        aria-label={`Page ${page}`}
      >
        <Text text={page} size="md" weight="medium" />
      </button>
    );
  };

  return (
    <nav className={classNames(styles.pagination, className)} aria-label="Pagination">
      <div className={styles.pages}>
        {renderArrow(currentPage - 1, "prev", currentPage <= 1)}

        {pages.map((page, index) => {
          if (page === "ellipsis") {
            return (
              <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                <Text text="..." size="md" />
              </span>
            );
          }
          return renderPage(page);
        })}

        {renderArrow(currentPage + 1, "next", currentPage >= totalPages)}
      </div>
    </nav>
  );
};

export default Pagination;
export type { PaginationProps };
